/* ==========================================================================
   Satjeevan — Product FBT Carousel JS (product-fbt-carousel.js)

   Responsibilities:
     1. Arrow-button navigation (scrolls by one card width + gap, smooth,
        GPU-accelerated native scroll), disabled at each boundary.
     2. Keyboard navigation (ArrowLeft / ArrowRight) when the track is focused.
     3. Manual drag/swipe horizontal scrolling for mouse users (touch devices
        already get native swipe via `overflow-x: auto`).
     4. Instant client-side variant switching per card: updates price,
        compare-at price, sale badge, product image, hidden variant id input,
        availability, and the Size/Weight/Quantity label — all without a
        network request, since every variant's data is embedded as JSON at
        render time.
     5. Optional autoplay (Theme Editor "Autoplay" setting), paused on
        hover/focus/touch so it never fights a shopper mid-interaction.

   None of the above run when the section's "Enable Slider" setting is off
   (the track then carries the .sj-pfbt-track--grid class and products just
   wrap in a static grid instead) — variant switching (4) still applies
   either way, since that has nothing to do with the carousel mechanics.

   Adding to cart itself is handled entirely by Horizon's existing
   <product-form-component> / <add-to-cart-component> custom elements
   (assets/product-form.js, loaded globally) — this file never touches
   /cart/add.js directly, avoiding any duplicate cart logic.
   ========================================================================== */

(function () {
  'use strict';

  var DRAG_INIT_ATTR = 'data-sj-pfbt-drag-init';
  var NAV_INIT_ATTR = 'data-sj-pfbt-nav-init';
  var VARIANT_INIT_ATTR = 'data-sj-pfbt-variant-init';

  /* -----------------------------------------------------------------------
     Size / Weight / Quantity label detection — mirrors the shared Liquid
     logic in snippets/product-fbt-card.liquid exactly.
     ----------------------------------------------------------------------- */
  function detectUnitLabel(value) {
    var v = (value || '').toLowerCase();
    if (v.indexOf('ml') !== -1) return 'Size';
    if (v.indexOf('kg') !== -1 || v.indexOf('gm') !== -1 || v.indexOf('g') !== -1) return 'Weight';
    return 'Quantity';
  }

  /* -----------------------------------------------------------------------
     1 + 3. Arrow navigation, boundary disabling, and drag-to-scroll
     ----------------------------------------------------------------------- */
  function initCarousels() {
    var tracks = document.querySelectorAll('.sj-pfbt-track:not([' + NAV_INIT_ATTR + '])');

    tracks.forEach(function (track) {
      track.setAttribute(NAV_INIT_ATTR, '1');
      track.setAttribute(DRAG_INIT_ATTR, '1');

      // Grid mode ("Enable Slider" off): products wrap statically, no
      // carousel mechanics apply.
      if (track.classList.contains('sj-pfbt-track--grid')) return;

      var section = track.closest('.sj-pfbt-section');
      if (!section) return;

      var prevBtn = section.querySelector('[data-sj-pfbt-prev]');
      var nextBtn = section.querySelector('[data-sj-pfbt-next]');

      function step() {
        var card = track.querySelector('.sj-pfbt-card');
        if (!card) return track.clientWidth * 0.8;
        var style = window.getComputedStyle(track);
        var gap = parseFloat(style.columnGap || style.gap || '18') || 18;
        return card.getBoundingClientRect().width + gap;
      }

      function updateBoundaryState() {
        var maxScroll = track.scrollWidth - track.clientWidth - 1;
        if (prevBtn) prevBtn.disabled = track.scrollLeft <= 0;
        if (nextBtn) nextBtn.disabled = track.scrollLeft >= maxScroll;
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          track.scrollBy({ left: -step(), behavior: 'smooth' });
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          track.scrollBy({ left: step(), behavior: 'smooth' });
        });
      }

      track.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          track.scrollBy({ left: step(), behavior: 'smooth' });
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          track.scrollBy({ left: -step(), behavior: 'smooth' });
        }
      });

      track.addEventListener('scroll', updateBoundaryState, { passive: true });
      window.addEventListener('resize', updateBoundaryState);
      updateBoundaryState();

      /* Mouse drag-to-scroll (desktop only; touch gets native swipe) */
      var isDown = false;
      var startX = 0;
      var startScroll = 0;
      var moved = false;

      track.addEventListener('mousedown', function (e) {
        // Prevent the browser's native drag-ghost gesture (links/images) from
        // fighting the custom mousemove-based scroll below. Form controls are
        // excluded so quantity-input focus and button/chip clicks still work.
        if (!e.target.closest('input, textarea, select, button')) {
          e.preventDefault();
        }
        isDown = true;
        moved = false;
        track.classList.add('dragging');
        startX = e.pageX;
        startScroll = track.scrollLeft;
      });

      window.addEventListener('mouseup', function () {
        isDown = false;
        track.classList.remove('dragging');
      });

      window.addEventListener('mousemove', function (e) {
        if (!isDown) return;
        e.preventDefault();
        var dx = e.pageX - startX;
        if (Math.abs(dx) > 4) moved = true;
        track.scrollLeft = startScroll - dx;
      });

      track.addEventListener(
        'click',
        function (e) {
          if (moved) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true
      );
    });
  }

  /* -----------------------------------------------------------------------
     5. Autoplay — only for tracks with data-autoplay="true" (Enable Slider
     must also be on, since the section only ever sets this attribute in
     that case). Pauses on hover, focus, and touch; loops back to the start
     once it reaches the end.
     ----------------------------------------------------------------------- */
  var AUTOPLAY_INIT_ATTR = 'data-sj-pfbt-autoplay-init';

  function initAutoplay() {
    var tracks = document.querySelectorAll(
      '.sj-pfbt-track[data-autoplay="true"]:not([' + AUTOPLAY_INIT_ATTR + '])'
    );

    tracks.forEach(function (track) {
      track.setAttribute(AUTOPLAY_INIT_ATTR, '1');

      var speedSeconds = parseFloat(track.getAttribute('data-autoplay-speed')) || 5;
      var timer = null;
      var paused = false;

      function step() {
        var card = track.querySelector('.sj-pfbt-card');
        if (!card) return track.clientWidth * 0.8;
        var style = window.getComputedStyle(track);
        var gap = parseFloat(style.columnGap || style.gap || '18') || 18;
        return card.getBoundingClientRect().width + gap;
      }

      function tick() {
        if (paused) return;
        var maxScroll = track.scrollWidth - track.clientWidth - 1;
        if (track.scrollLeft >= maxScroll) {
          track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          track.scrollBy({ left: step(), behavior: 'smooth' });
        }
      }

      function start() {
        stop();
        timer = window.setInterval(tick, speedSeconds * 1000);
      }
      function stop() {
        if (timer) {
          window.clearInterval(timer);
          timer = null;
        }
      }

      track.addEventListener('mouseenter', function () { paused = true; });
      track.addEventListener('mouseleave', function () { paused = false; });
      track.addEventListener('touchstart', function () { paused = true; }, { passive: true });
      track.addEventListener('touchend', function () { paused = false; });
      track.addEventListener('focusin', function () { paused = true; });
      track.addEventListener('focusout', function () { paused = false; });
      document.addEventListener('visibilitychange', function () {
        paused = document.hidden;
      });

      start();
    });
  }

  /* -----------------------------------------------------------------------
     4. Variant chip switching
     ----------------------------------------------------------------------- */
  function initVariantSwitching() {
    var cards = document.querySelectorAll(
      '[data-sj-pfbt-card][data-has-variant-chips="true"]:not([' + VARIANT_INIT_ATTR + '])'
    );

    cards.forEach(function (card) {
      card.setAttribute(VARIANT_INIT_ATTR, '1');

      var variantsScript = card.querySelector('[data-pfbt-variants]');
      if (!variantsScript) return;

      /** @type {Array<Object>} */
      var variants;
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (err) {
        return;
      }

      var chips = card.querySelectorAll('[data-pfbt-chip]');
      var priceEl = card.querySelector('[data-pfbt-price]');
      var compareEl = card.querySelector('[data-pfbt-compare]');
      var badgeEl = card.querySelector('[data-pfbt-badge]');
      var labelEl = card.querySelector('[data-pfbt-label]');
      var imageEl = card.querySelector('.sj-pfbt-image');
      var variantInput = card.querySelector('[data-pfbt-variant-input]');
      var atcButton = card.querySelector('.sj-pfbt-atc-btn');
      var atcTextNode = atcButton
        ? atcButton.querySelector('.add-to-cart-text__content span span')
        : null;

      var textAddToCart = card.getAttribute('data-text-add-to-cart') || 'Add to cart';
      var textSoldOut = card.getAttribute('data-text-sold-out') || 'Sold out';

      function applyVariant(variant, chipEl) {
        if (!variant) return;

        if (priceEl) priceEl.textContent = variant.price;

        var showCompare = Number(variant.compareRaw) > Number(variant.priceRaw);
        if (compareEl) {
          if (showCompare) {
            compareEl.textContent = variant.compare;
            compareEl.removeAttribute('hidden');
          } else {
            compareEl.textContent = '';
            compareEl.setAttribute('hidden', '');
          }
        }
        if (badgeEl) {
          if (showCompare) {
            badgeEl.removeAttribute('hidden');
          } else {
            badgeEl.setAttribute('hidden', '');
          }
        }

        if (variantInput) variantInput.value = variant.id;

        if (imageEl && variant.image) {
          imageEl.src = variant.image;
          imageEl.removeAttribute('srcset');
        }

        if (labelEl && chipEl) {
          labelEl.textContent = detectUnitLabel(chipEl.textContent).toUpperCase();
        }

        chips.forEach(function (c) {
          var isSelected = c === chipEl;
          c.classList.toggle('sel', isSelected);
          c.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        if (atcButton) {
          atcButton.disabled = !variant.available;
        }
        if (atcTextNode) {
          atcTextNode.textContent = variant.available ? textAddToCart : textSoldOut;
        }
      }

      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          var variantId = chip.getAttribute('data-variant-id');
          var variant = null;
          for (var i = 0; i < variants.length; i++) {
            if (String(variants[i].id) === String(variantId)) {
              variant = variants[i];
              break;
            }
          }
          applyVariant(variant, chip);
        });
      });
    });
  }

  /* -----------------------------------------------------------------------
     Bootstrap
     ----------------------------------------------------------------------- */
  function init() {
    initCarousels();
    initVariantSwitching();
    initAutoplay();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify Theme Editor live-reload support
  document.addEventListener('shopify:section:load', init);
})();
